/**
 * Profile > My Business Cards
 * Each user manages their own personal business card templates.
 */
import { useState, useEffect } from 'react'
import {
  listBusinessCards,
  createBusinessCard,
  updateBusinessCard,
  deleteBusinessCard,
} from '../../services/crm'
import type { BusinessCardTemplate } from '../../types'
import Modal from '../../components/Modal'
import SendCardModal from '../../components/SendCardModal'
import { useAuth } from '../../services/auth'
import { CardForm, BusinessCardPreview } from '../../components/businessCards'
import {
  EMPTY_FORM,
  personalCardDefaults,
  type CardFormState,
} from '../../utils/businessCardForm'

export default function MyBusinessCardsPage() {
  const { user } = useAuth()

  const [myCards, setMyCards] = useState<BusinessCardTemplate[]>([])
  const [companyCards, setCompanyCards] = useState<BusinessCardTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  // Send this card to a typed address (no CRM record involved).
  const [sendCard, setSendCard] = useState<BusinessCardTemplate | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [editingCard, setEditingCard] = useState<BusinessCardTemplate | null>(null)
  const [form, setForm] = useState<CardFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  const [deleteTarget, setDeleteTarget] = useState<BusinessCardTemplate | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    setLoadError('')
    try {
      const all = await listBusinessCards()
      setMyCards(all.filter((c) => c.card_type === 'personal'))
      setCompanyCards(all.filter((c) => c.card_type === 'company'))
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load cards')
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setEditingCard(null)
    setForm(personalCardDefaults(user))
    setFormError('')
    setShowModal(true)
  }

  function openEdit(card: BusinessCardTemplate) {
    setEditingCard(card)
    setForm({
      name: card.name,
      sender_name: card.sender_name,
      title: card.title ?? '',
      email: card.email ?? '',
      phone: card.phone ?? '',
      greeting_text: card.greeting_text ?? '',
    })
    setFormError('')
    setShowModal(true)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setFormError('')
    try {
      if (editingCard) {
        const updated = await updateBusinessCard(editingCard.id, {
          name: form.name.trim(),
          sender_name: form.sender_name.trim(),
          title: form.title.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          greeting_text: form.greeting_text.trim() || undefined,
        })
        setMyCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      } else {
        const created = await createBusinessCard({
          name: form.name.trim(),
          card_type: 'personal',
          sender_name: form.sender_name.trim(),
          title: form.title.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          greeting_text: form.greeting_text.trim() || undefined,
        })
        setMyCards((prev) => [...prev, created])
      }
      setShowModal(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteBusinessCard(deleteTarget.id)
      setMyCards((prev) => prev.filter((c) => c.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Business Cards</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create personal card templates to send when contacting leads and clients.
          </p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          + New Card
        </button>
      </div>

      {loadError && <div className="alert alert-error mb-4">{loadError}</div>}

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <>
          {/* Personal cards */}
          {myCards.length === 0 ? (
            <div className="card text-center text-gray-500 py-12 mb-6">
              You don't have any personal business cards yet.{' '}
              <button onClick={openCreate} className="text-blue-600 underline">
                Create your first one.
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              {myCards.map((card) => (
                <BusinessCardPreview
                  key={card.id}
                  card={card}
                  onEdit={() => openEdit(card)}
                  onDelete={() => setDeleteTarget(card)}
                  onToggleActive={async () => {
                    try {
                      const updated = await updateBusinessCard(card.id, { is_active: !card.is_active })
                      setMyCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
                    } catch (err) {
                      alert(err instanceof Error ? err.message : 'Failed to update card')
                    }
                  }}
                  onSend={() => setSendCard(card)}
                />
              ))}
            </div>
          )}

          {/* Company cards (read-only) */}
          {companyCards.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-700 mb-3">
                Company Cards <span className="text-sm font-normal text-gray-400">(shared, managed by admin)</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {companyCards.map((card) => (
                  <div key={card.id} className="card flex flex-col gap-3">
                    <div className="flex gap-0 rounded border border-gray-200 overflow-hidden">
                      <div className="w-1.5 bg-blue-900 flex-shrink-0" />
                      <div className="p-3 flex-1 min-w-0">
                        <div className="font-bold text-blue-900 text-sm leading-tight truncate">{card.sender_name}</div>
                        {card.title && <div className="text-xs text-gray-500 italic truncate">{card.title}</div>}
                        <div className="mt-2 space-y-0.5">
                          {card.email && (
                            <div className="text-xs text-gray-600 truncate">
                              <span className="font-semibold text-blue-900 uppercase text-[10px] tracking-wide mr-1">Email</span>
                              {card.email}
                            </div>
                          )}
                          {card.phone && (
                            <div className="text-xs text-gray-600">
                              <span className="font-semibold text-blue-900 uppercase text-[10px] tracking-wide mr-1">Phone</span>
                              {card.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 font-medium truncate">{card.name}</div>
                    {/* A company card is not yours to edit, but it is yours to
                        send — that is the whole point of it being shared. */}
                    <button className="btn btn-primary btn-sm" onClick={() => setSendCard(card)}>
                      Send
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {sendCard && (
        <SendCardModal
          fixedCard={sendCard}
          onClose={() => setSendCard(null)}
        />
      )}

      {/* Create / Edit modal */}
      {showModal && (
        <Modal
          titleId="my-business-card-modal-title"
          title={editingCard ? 'Edit Business Card' : 'New Business Card'}
          onClose={() => setShowModal(false)}
          showCloseButton
        >
          <CardForm
            form={form}
            setForm={setForm}
            onSubmit={handleSave}
            saving={saving}
            error={formError}
            submitLabel={editingCard ? 'Save Changes' : 'Create Card'}
          />
        </Modal>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <Modal
          titleId="delete-my-card-title"
          title="Delete Card"
          onClose={() => setDeleteTarget(null)}
          showCloseButton
        >
          <p className="text-gray-700 mb-4">
            Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
